import * as React from 'react';
import { PercentageOutlined, RiseOutlined } from '@ant-design/icons';
import Alert from 'antd/lib/alert';
import Avatar from 'antd/lib/avatar';
import Card from 'antd/lib/card';
import Descriptions from 'antd/lib/descriptions';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Progress from 'antd/lib/progress';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { StepRunUpdatable, UpdateExpoEASRolloutRequest } from '@/actions/stepRun';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { ExpoEASRolloutData, IntegrationType, RolloutAction, RunwayResponseType } from '@/types';
import { StepRunComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable } from '@/utils/hooks/helpers';

interface ExpoEASRolloutStepProps {
    stepRunComponentProps: StepRunComponentProps;
    data?: ExpoEASRolloutData;
}

const rolloutActionButtonStyle = { width: 220 };
const [expoEASLogo, expoEASLogoAlt] = iconAndAltForIntegrationType(IntegrationType.ExpoEAS);

export const ExpoEASRolloutStep = ({ stepRunComponentProps, data }: ExpoEASRolloutStepProps) => {
    const currentPercentage = data?.currentRolloutPercentage;
    const [updateModalOpen, setUpdateModalOpen] = React.useState(false);
    const [targetPercentage, setTargetPercentage] = React.useState<number | null>(
        currentPercentage != null && currentPercentage < 100 ? currentPercentage + 1 : null,
    );
    const [updateRollout, submitting, responseType, error] = useCallUpdatable(
        StepRunUpdatable.UpdateExpoEASRollout,
        stepRunComponentProps,
    );

    React.useEffect(() => {
        setTargetPercentage(currentPercentage != null && currentPercentage < 100 ? currentPercentage + 1 : null);
    }, [currentPercentage]);

    React.useEffect(() => {
        if (responseType === RunwayResponseType.Success) {
            setUpdateModalOpen(false);
            globalMessage.success('Expo rollout updated.');
        }
    }, [responseType]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    if (currentPercentage == null) {
        return (
            <Alert
                type="info"
                showIcon
                message="Rollout pending"
                description="Publish the Expo Release step to the Production channel before progressing this rollout."
            />
        );
    }

    const complete = currentPercentage === 100;
    const validTarget =
        targetPercentage != null &&
        Number.isInteger(targetPercentage) &&
        targetPercentage > currentPercentage &&
        targetPercentage <= 100;

    const updatePercentage = (percentage: number) => {
        if (complete || percentage <= currentPercentage || percentage > 100) return;
        const request: UpdateExpoEASRolloutRequest = {
            actionType: RolloutAction.UpdatePercentage,
            targetPercentage: percentage,
        };
        updateRollout(request);
    };

    const openUpdateModal = () => {
        setTargetPercentage(currentPercentage + 1);
        setUpdateModalOpen(true);
    };

    return (
        <Card
            title={
                <Space size="small">
                    <Avatar
                        src={expoEASLogo}
                        alt={expoEASLogoAlt}
                        size={24}
                        shape="square"
                        style={{ border: 'none' }}
                    />
                    <span>Expo EAS rollout</span>
                </Space>
            }
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Descriptions column={1} size="small">
                    {data?.sourceGroup && (
                        <Descriptions.Item label="Source group">
                            <Space>
                                <Text strong>{data.sourceGroup.branchName}</Text>
                                <Text type="secondary">Runtime {data.sourceGroup.runtimeVersion}</Text>
                            </Space>
                        </Descriptions.Item>
                    )}
                    {data?.productionGroupId && (
                        <Descriptions.Item label="Production group">
                            <Text code>{data.productionGroupId}</Text>
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Production channel">
                        {data?.productionChannel || 'Production'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Current rollout">
                        <Progress
                            percent={currentPercentage}
                            format={() => <Text style={{ color: antGrey.grey8 }}>{currentPercentage}%</Text>}
                            size="small"
                            strokeColor={runwayColors.primary}
                            trailColor={antGrey.grey5}
                            style={{ width: 420, maxWidth: '100%' }}
                        />
                    </Descriptions.Item>
                </Descriptions>
                {!complete && (
                    <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            <PermissionedActionButton
                                actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                releaseId={stepRunComponentProps.releaseId}
                                icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                                loading={submitting}
                                disabled={submitting}
                                customDisallowedMessage="You do not have permission to edit rollouts"
                                style={rolloutActionButtonStyle}
                                onClick={openUpdateModal}
                            >
                                Update rollout
                            </PermissionedActionButton>
                            <Popconfirm
                                title="Are you sure you want to release to 100% now?"
                                okText="Yes, release to 100% now."
                                placement="topRight"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => updatePercentage(100)}
                            >
                                <PermissionedActionButton
                                    actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                    releaseId={stepRunComponentProps.releaseId}
                                    icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                                    loading={submitting}
                                    disabled={submitting}
                                    customDisallowedMessage="You do not have permission to release to 100% of users"
                                    style={rolloutActionButtonStyle}
                                >
                                    Release to 100% now
                                </PermissionedActionButton>
                            </Popconfirm>
                        </div>
                        <Modal
                            title="Update rollout"
                            open={updateModalOpen}
                            okText="Update rollout"
                            confirmLoading={submitting}
                            okButtonProps={{ disabled: !validTarget }}
                            cancelButtonProps={{ disabled: submitting }}
                            closable={!submitting}
                            maskClosable={!submitting}
                            onCancel={() => setUpdateModalOpen(false)}
                            onOk={() => validTarget && updatePercentage(targetPercentage)}
                        >
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Text>Choose a percentage above the current {currentPercentage}% rollout.</Text>
                                <InputNumber
                                    aria-label="Target rollout percentage"
                                    min={currentPercentage + 1}
                                    max={100}
                                    precision={0}
                                    value={targetPercentage}
                                    onChange={(value) => setTargetPercentage(typeof value === 'number' ? value : null)}
                                    addonAfter="%"
                                    disabled={submitting}
                                    autoFocus
                                />
                            </Space>
                        </Modal>
                    </>
                )}
            </Space>
        </Card>
    );
};
